import { getByID } from '@adornis/baseql/operations/mongo.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { MailAttachment } from '@adornis/mail-client/mail-attachment-entity.js';
import { renderMailHeaders } from '@adornis/mail-client/mail-detail.js';
import { MailEntity } from '@adornis/mail-client/mail-entity.js';
import { html, nothing } from 'lit';

// A single downloadable attachment, shown as a quiet chip. Mirrors the (non-exported) chip in
// @adornis/mail-client/mail-detail but styled to match the redesigned panel.
const AttachmentChip = stateful((props: { fileID: string }) => {
  // completeName is a getter derived from fileName + extension, and the serve link needs the
  // extension too — so select all three, not just completeName (which would render empty).
  const { value: file } = useQuery(
    () => getByID(MailAttachment, props.fileID)({ _id: 1, meta: { fileName: 1, extension: 1, completeName: 1 } }),
    [props.fileID],
  );
  if (!file) return nothing;
  return html`
    <a
      href="${file.getServeLink()}"
      download
      class="inline-flex items-center gap-1.5 rounded-lg border border-base-300 bg-base-200/50 px-2.5 py-1 text-xs text-base-content/70 hover:bg-base-200 transition"
      title="Download ${file.meta?.completeName}"
    >
      <x-icon class="text-sm">download</x-icon>
      ${file.meta?.completeName}
    </a>
  `;
});

// Left panel of the detail view: the original mail. Its own header (subject + From/To +
// attachments) over an independently scrolling body, so the operator can keep the source in
// view while working through the extraction on the right.
export const MailContentPanel = stateful((props: { mailID: string }) => {
  const { value: mail, loading } = useQuery(
    () =>
      getByID(
        MailEntity,
        props.mailID,
      )({
        subject: 1,
        from: { address: 1, name: 1 },
        to: { address: 1, name: 1 },
        cc: { address: 1, name: 1 },
        bcc: { address: 1, name: 1 },
        replyTo: { address: 1, name: 1 },
        receivedAt: 1,
        htmlContent: 1,
        textContent: 1,
        attachments: 1,
      }),
    [props.mailID],
  );

  return html`
    <div class="flex h-full min-h-0 flex-col bg-base-100">
      <div class="px-6 pt-5 pb-3 border-b border-base-200">
        <div class="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-wider text-base-content/40">
          <x-icon class="text-sm">mail</x-icon> Mailinhalt
        </div>
        ${loading
          ? html`<div class="mt-3 text-sm text-base-content/40">Lädt…</div>`
          : mail
            ? html`
                <div class="mt-3">${renderMailHeaders(mail)}</div>
                ${mail.attachments?.length
                  ? html`<div class="mt-3 flex flex-wrap gap-2">
                      ${mail.attachments.map(id => AttachmentChip({ fileID: id }))}
                    </div>`
                  : nothing}
              `
            : html`<div class="mt-3 text-sm text-error">Mail nicht gefunden.</div>`}
      </div>
      <!-- Body fills the remaining height: HTML mails render in a full-height sandboxed iframe
           (scrolls internally), plain-text mails in a full-height scrollable block. -->
      <div class="flex-1 min-h-0">
        ${mail
          ? mail.htmlContent
            ? html`<div class="h-full px-6 py-5">
                <iframe
                  class="w-full h-full border-0"
                  sandbox="allow-same-origin allow-scripts"
                  srcdoc="${mail.htmlContent}"
                ></iframe>
              </div>`
            : html`<div
                class="h-full overflow-y-auto whitespace-pre-wrap px-6 py-5 text-sm leading-relaxed text-base-content/80"
              >
                ${mail.textContent ?? ''}
              </div>`
          : nothing}
      </div>
    </div>
  `;
});
